import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calendar, CheckCircle2, DollarSign, Wallet, Percent, Star, MessageSquare,
  Gift, Clock, AlertCircle, Briefcase, MapPin, Phone, Mail, User as UserIcon
} from 'lucide-react';
import { providersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';

const StatCard = ({ icon: Icon, label, value, color = 'blue', suffix = '' }) => (
  <div
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow"
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 flex items-center justify-center`}>
        <Icon size={20} className={`text-${color}-600`} />
      </div>
    </div>
    <p className="text-xs text-gray-500 mb-1 font-medium">{label}</p>
    <p className="text-2xl font-bold text-gray-900">
      {value}
      {suffix && <span className="text-sm font-normal text-gray-500 ml-1">{suffix}</span>}
    </p>
  </div>
);

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const isRTL = i18n.language?.startsWith('ar');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'provider') {
      setLoading(false);
      return;
    }
    providersAPI.getDashboard()
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || t('common.error')))
      .finally(() => setLoading(false));
  }, [isAuthenticated, user, t]);

  if (!isAuthenticated || user?.role !== 'provider') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white pt-20 px-4">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-md">
          <AlertCircle size={48} className="text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t('dashboard.notAuthorized')}</h2>
          <button
            onClick={() => navigate('/')}
            data-testid="dashboard-go-home"
            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl px-6 py-3 transition"
          >
            {t('register.backHome')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20 px-4">
        <p className="text-red-600 font-medium">{error || t('common.error')}</p>
      </div>
    );
  }

  const { provider, trial, stats, bookings, reviews } = data;

  const statusLabel = (s) => {
    if (s === 'confirmed') return t('dashboard.bookings.statusConfirmed');
    if (s === 'completed') return t('dashboard.bookings.statusCompleted');
    if (s === 'cancelled') return t('dashboard.bookings.statusCancelled');
    return s;
  };

  const statusClass = (s) => {
    if (s === 'confirmed') return 'bg-blue-100 text-blue-700';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'cancelled') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/40 via-white to-white pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-gray-500 mb-1">{t('dashboard.welcome')},</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="dashboard-title">
              {provider.name}
            </h1>
          </div>
        </div>

        {/* Trial Banner */}
        {trial.active ? (
          <div
            data-testid="trial-banner-active"
            className="mb-8 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-2xl shadow-lg p-6 flex items-center gap-4 flex-wrap"
          >
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Gift size={28} />
            </div>
            <div className="flex-1 min-w-[200px]">
              <h3 className="font-bold text-lg mb-1">{t('dashboard.trialActiveTitle')}</h3>
              <p className="text-sm opacity-95">{t('dashboard.trialDaysLeft', { days: trial.days_remaining })}</p>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <Clock size={18} />
              <span className="font-bold text-sm">{trial.days_remaining}</span>
              <span className="text-xs opacity-90">/ 90</span>
            </div>
          </div>
        ) : (
          <div
            data-testid="trial-banner-expired"
            className="mb-8 bg-orange-50 border-2 border-orange-200 rounded-2xl p-5 flex items-start gap-3"
          >
            <AlertCircle size={24} className="text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-orange-900 font-medium">{t('dashboard.trialExpired')}</p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Calendar} label={t('dashboard.stats.totalBookings')} value={stats.total_bookings} color="blue" />
          <StatCard icon={CheckCircle2} label={t('dashboard.stats.pendingBookings')} value={stats.pending_bookings} color="indigo" />
          <StatCard icon={DollarSign} label={t('dashboard.stats.totalEarnings')} value={stats.total_earnings} suffix="DH" color="green" />
          <StatCard icon={Wallet} label={t('dashboard.stats.netEarnings')} value={stats.net_earnings} suffix="DH" color="emerald" />
          <StatCard icon={Percent} label={t('dashboard.stats.totalCommission')} value={stats.total_commission} suffix="DH" color="orange" />
          <StatCard icon={Star} label={t('dashboard.stats.rating')} value={stats.rating} suffix="/ 5" color="yellow" />
          <StatCard icon={MessageSquare} label={t('dashboard.stats.reviewsCount')} value={stats.reviews_count} color="purple" />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bookings */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-5 border-b border-gray-100">
              <h2 className="font-bold text-lg text-gray-900" data-testid="bookings-title">
                {t('dashboard.bookings.title')}
              </h2>
            </div>
            {bookings.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-700 mb-1">{t('dashboard.bookings.empty')}</p>
                <p className="text-sm text-gray-500">{t('dashboard.bookings.emptyHint')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bookings-table">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="text-start px-4 py-3 font-semibold">{t('dashboard.bookings.columns.date')}</th>
                      <th className="text-start px-4 py-3 font-semibold">{t('dashboard.bookings.columns.tasks')}</th>
                      <th className="text-start px-4 py-3 font-semibold">{t('dashboard.bookings.columns.amount')}</th>
                      <th className="text-start px-4 py-3 font-semibold">{t('dashboard.bookings.columns.commission')}</th>
                      <th className="text-start px-4 py-3 font-semibold">{t('dashboard.bookings.columns.status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {bookings.map((b) => (
                      <tr key={b.id} className="hover:bg-gray-50 transition" data-testid={`booking-row-${b.id}`}>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="font-medium">{b.date}</div>
                          <div className="text-xs text-gray-400">{b.time}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{b.hours}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">{b.subtotal} DH</td>
                        <td className="px-4 py-3">
                          {b.total_commission > 0 ? (
                            <span className="text-orange-600 font-medium">{b.total_commission} DH</span>
                          ) : (
                            <span className="text-green-600 font-medium text-xs">0 DH ✨</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full ${statusClass(b.status)}`}>
                            {statusLabel(b.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Profile + Reviews */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="font-bold text-lg text-gray-900 mb-4" data-testid="profile-title">
                {t('dashboard.profile.title')}
              </h2>
              <div className="flex items-center gap-4 mb-5">
                <img src={provider.photo} alt={provider.name} className="w-16 h-16 rounded-full object-cover" />
                <div>
                  <p className="font-semibold text-gray-900">{provider.name}</p>
                  <p className="text-sm text-gray-500">{provider.skill}</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 text-gray-600">
                  <Briefcase size={16} className="text-gray-400" />
                  <span>{t(`categoryNames.${provider.category}`, provider.skill)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin size={16} className="text-gray-400" />
                  <span>{provider.city}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <DollarSign size={16} className="text-gray-400" />
                  <span className="font-semibold">{provider.price} DH</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone size={16} className="text-gray-400" />
                  <span>{provider.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail size={16} className="text-gray-400" />
                  <span className="truncate">{provider.email}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="font-bold text-lg text-gray-900 mb-4">{t('dashboard.reviews.title')}</h2>
              {reviews.length === 0 ? (
                <div className="py-8 text-center">
                  <Star size={32} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">{t('dashboard.reviews.empty')}</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviews.slice(0, 5).map((r) => (
                    <div key={r.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                          <UserIcon size={14} className="text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-800">{r.client_name || 'Client'}</span>
                        <div className="flex items-center gap-0.5 ml-auto">
                          <Star size={12} className="text-yellow-400 fill-yellow-400" />
                          <span className="text-xs font-bold">{r.rating}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 pl-10">{r.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
